'use client';
import React, { useEffect, useRef, useState } from 'react';
import { siteSettings as staticSettings } from '@/data/siteData';

interface Settings {
  aboutImage: string;
  aboutText: string[];
  cvUrl: string;
}

export default function AboutSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [settings, setSettings] = useState<Settings>({
    aboutImage: '',
    aboutText: staticSettings.aboutText,
    cvUrl: staticSettings.cvUrl,
  });
  const [imageLoading, setImageLoading] = useState<boolean>(true);

  useEffect(() => {
    fetch('/api/settings')
      .then((res) => res.json())
      .then((data) => {
        if (data?.settings) {
          setSettings({
            aboutImage: data.settings.aboutImage || staticSettings.aboutImage,
            aboutText: data.settings.aboutText || staticSettings.aboutText,
            cvUrl: data.settings.cvUrl || staticSettings.cvUrl,
          });
        }
      })
      .catch(() => {})
      .finally(() => setImageLoading(false));
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('aos-animate');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="about"
      className="p-4 sm:p-6 lg:p-8 text-[#48438e] text-shadow-sm"
    >
      <div
        ref={sectionRef}
        className="mt-24 space-y-8 fade-in-section"
      >
        <div className="flex flex-col gap-1 items-center">
          <h2 className="text-4xl">Tentang Saya</h2>
          <p className="text-lg">Perkenalkan</p>
        </div>
        <div className="flex flex-col md:flex-row gap-4 md:gap-10 items-center justify-center">
          {/* About Image */}
          <div className="max-w-[20rem]">
            {imageLoading ? (
              <div className="w-72 h-80 bg-gray-200 animate-pulse rounded-lg" />
            ) : settings.aboutImage ? (
              <img
                src={settings.aboutImage}
                alt="Foto Muhammad Fajrul Amin"
                className="rounded-lg"
              />
            ) : null}
          </div>
          {/* About Text */}
          <div className="max-w-sm md:max-w-lg flex flex-col gap-4 items-center md:items-start justify-around">
            <div className="text-center md:text-left">
              {settings.aboutText?.map((paragraph, index) => (
                <React.Fragment key={index}>
                  <p>{paragraph}</p>
                  {index < (settings.aboutText?.length ?? 0) - 1 && <br />}
                </React.Fragment>
              ))}
              <a href={settings.cvUrl} download="Fajrul-Resume.pdf" target="_blank" rel="noopener noreferrer">
                <div className="h-10 rounded-md my-2">
                  <div className="flex h-full w-full items-center justify-center rounded-md bg-[#48438e]">
                    <span
                      className="font-semibold text-transparent bg-clip-text"
                      style={{ backgroundImage: 'linear-gradient(90deg, #74A9F5, #E7C2F9 65%)' }}
                    >
                      Download Cv
                    </span>
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
